import React from 'react';
import ErrorMessage from './ErrorMessage';
import Loading from './loading';
import TabListTable from '../Elements/TabList/TabListTableFigma';
import ButtonTypeEnum from '../Enums/ButtonTypeEnum';
import ButtonColorsEnum from '../Enums/ButtonColorsEnum';
import IconPositionEnum from '../Enums/IconPositionEnum';
import FigmaButtonRow from './FigmaButtonRow';
import UserGuideTitle from './UserGuideTitle';

class ShowEntityData extends React.Component {
  static defaultProps = {
    itemId: null,
    entityName: '',
    type: null,
    serviceGetByIdFunction: (id) => {},
    emptyFieldsEnabled: false,
    itemChanged: () => {},
    showRefreshButton: true,
    additionalButtons: [],
    useItemTypeTitle: false,
    // Set false when the hosting page's shell header already shows the same title
    // (e.g. a routed details page) — keeps the action buttons, drops the duplicate text.
    showHead: true,
    // Set true inside a tab panel rather than on a routed page: drops the page-level `.contentwrapper` and
    // its -20px margin (tuned for the fixed 80px header), which would pull content over the tab-label bar.
    embedded: false,
    // Set to the page's url slug only when the hosting page hides the shell header, so this
    // head's title takes over the shell's user-guide link. Elsewhere the shell already has it.
    userGuideUrl: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      data: [],
      loading: false,
      errorHappened: false,
    };
  }

  componentDidMount() {
    this.populateData();
  }

  componentDidUpdate(prevProps) {
    if (this.props.itemId !== prevProps.itemId || this.props.forceRefresh !== prevProps.forceRefresh) {
      this.populateData(this.props.forceRefresh !== prevProps.forceRefresh);
    }
  }

  async populateData(clearCache = false) {
    const itemId = this.props.itemId;
    if (!itemId) {
      return;
    }

    this.setState({
      loading: true,
    });

    try {
      const result = await this.props.serviceGetByIdFunction(itemId, clearCache);

      // Null check FIRST - below `result.error` it was unreachable, since that line threw on a null result.
      if (!result) {
        this.setState({
          data: null,
          loading: false,
          errorHappened: false,
        });
        return;
      }

      if (result.error) {
        ErrorMessage.SetErrorMessage(result.error);
        this.setState({
          loading: false,
        });
        return;
      }

      const convertedResult = new this.props.type(result);

      this.setState({
        data: convertedResult,
        loading: false,
        errorHappened: false,
      });
      this.props.itemChanged(convertedResult);
    } catch (error) {
      ErrorMessage.SetErrorMessage(error.message);
      this.setState({
        loading: false,
        errorHappened: true,
      });
    }
  }

  renderData() {
    if (!this.state.data) {
      return (
        <div className='bravo-ds'>
          <div className='detail-body'>There is no such data.</div>
        </div>
      );
    }
    const actions = this.renderActions();
    return (
      <div className='bravo-ds'>
        {(this.props.showHead || actions) && (
          <div className='detail-head'>
            {this.props.showHead && (
              <div className='fh-txt'>
                <div className='eyebrow'>{this.props.entityName}</div>
                <h1>
                  {this.props.userGuideUrl ? (
                    <UserGuideTitle
                      title={this.props.entityName}
                      url={this.props.userGuideUrl}
                    />
                  ) : (
                    this.props.entityName
                  )}
                </h1>
              </div>
            )}
            <div className='sp' />
            {/* No ID pill here: ShowEntityData renders inside routed/tab pages that
                already show the id in their own page header. The popup path keeps its
                pill via OverWindow. */}
            {actions}
          </div>
        )}
        <TabListTable
          caption={this.getDisplayCaption()}
          data={this.state.data}
          moneyFields={this.props.type.MoneyFields}
          moneyFieldsSecondary={this.props.type.MoneyFieldsSecondary}
          dateFields={this.props.type.DateFields}
          dateOnlyFields={this.props.type.DateOnlyFields}
          currency={this.props.type.Currency()}
          currencySecondary={this.props.type.CurrencySecondary()}
          fieldsToShow={this.props.type.FieldsToShowInListTable}
          boolFields={this.props.type.BoolFields}
          type={this.props.type}
          emptyFieldsEnabled={this.props.emptyFieldsEnabled}
          popupEnabled={false}
          clickOnCancel={null}
          showCaption={false}
        />
      </div>
    );
  }
  getDisplayCaption() {
    // If the itemId is a string or number, add it to the display caption
    return typeof this.props.itemId === 'string' || typeof this.props.itemId === 'number'
      ? `${this.props.entityName} (${this.props.itemId})`
      : `${this.props.entityName}`;
  }

  renderActions() {
    let buttons = [...this.props.additionalButtons];
    buttons = buttons.filter((button) => {
      if (button.condition) {
        return button.condition(this.state.data);
      }
      return true;
    });

    if (this.props.showRefreshButton) {
      buttons.push({
        iconName: 'fa-solid fa-refresh',
        type: ButtonTypeEnum.NormalIconOnly,
        tooltip: 'Refresh',
        color: ButtonColorsEnum.Common,
        iconPosition: IconPositionEnum.Center,
        onClick: () => this.populateData(true),
      });
    }
    if (buttons.length === 0) {
      return false;
    }
    // The default `.MenuButtonList` hoists the row into a routed page's fixed header band (margin-top:-2000px);
    // in a tab that drags it onto the tab bar, so use the in-popup/in-tab class there (see FRONTEND.md).
    return (
      <FigmaButtonRow
        buttons={buttons}
        className={this.props.embedded ? 'InnerMenuButtonFigmaList' : 'MenuButtonList'}
      />
    );
  }

  render() {
    if (this.state.errorHappened) {
      return (
        <div
          className='contentwrapper h6'
          style={{
            marginTop: 20,
            marginLeft: 20,
            marginRight: 20,
          }}
        >
          {' '}
          The data couldn't be loaded due to an error.
        </div>
      );
    }
    // If there is no item ID, return a "There is no data" message.
    if (!this.props.itemId) return <div>There is no data.</div>;

    // If there is data to display, return the data.
    return (
      <div
        className={this.props.embedded ? undefined : 'contentwrapper'}
        style={this.props.embedded ? undefined : { marginTop: -20 }}
      >
        <Loading loading={this.state.loading} />
        {!this.state.loading && this.renderData()}
      </div>
    );
  }
}

export default ShowEntityData;
